// Undo a change of sign-in address, from the note sent to the old one
// (PLAN-2026-10-28-by-email §3.5, scenario A6, B-548).
//
// `/email-undo#<token>`, the token in the fragment and taken out of the bar
// once read. A NEW PASSWORD IN THE SAME STEP: whoever made the change knew the
// old one. Said before the press: this puts the old address back, signs out
// every device, and signs this one in.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { newPasswordRefusal, PASSWORD_RULE } from './shared/passwordRule';
import { useSession } from '../data/session';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { PasswordField, Text, tell } from '../kit/ui';

/** A token as `newToken` makes them: base64url, 43 characters for 32 bytes. */
const TOKEN = /^[A-Za-z0-9_-]{20,}$/;

type Stage = 'choose' | 'failed';

export function EmailUndoScreen() {
  const { undoEmailChange } = useSession();
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState<string | null>(() => {
    const raw = location.hash.replace(/^#/, '');
    return TOKEN.test(raw) ? raw : null;
  });
  const [stage, setStage] = useState<Stage>(token === null ? 'failed' : 'choose');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [why, setWhy] = useState<string>(
    'This page opens from the link in the note about a changed email.',
  );
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (location.hash !== '') navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  const save = async () => {
    if (pending || token === null) return;
    const refusal = newPasswordRefusal(password);
    if (refusal !== null) return setError(refusal);
    setPending(true);
    const outcome = await undoEmailChange(token, password);
    setPending(false);
    if (outcome.ok) {
      setPassword('');
      tell('Your email is back, with your new password. Every other device is signed out.', 'good');
      navigate('/', { replace: true });
      return;
    }
    // USED, ENDED OR RUN OUT: the card says so. Anything else — an address
    // held elsewhere, a password refused, no connection — stays here, the
    // undo still good.
    if (outcome.code === 'unauthorized') {
      setWhy(outcome.message);
      setStage('failed');
      return;
    }
    setError(outcome.message);
  };

  const steps: Record<Stage, WizardStep> = {
    choose: {
      id: 'choose',
      title: 'Undo the change',
      hint: 'This puts this address back, signs out every device, and signs you in here with a new password.',
      content: (
        <PasswordField
          label="New password"
          description={PASSWORD_RULE}
          value={password}
          onChange={(next) => {
            setError(null);
            setPassword(next);
          }}
          autoComplete="new-password"
        />
      ),
      error,
      doneLabel: pending ? 'Putting it back…' : 'Undo and sign in',
    },
    failed: {
      id: 'failed',
      title: 'That link will not work',
      hint: why,
      content: (
        <Text tone="secondary" size="sm" block>
          An undo works once, for 24 hours after the change. After that, the change stands.
        </Text>
      ),
      doneLabel: 'Done',
    },
  };

  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="Your email"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'choose') void save();
          else navigate('/', { replace: true });
        }}
        onCancel={stage === 'choose' ? () => navigate('/', { replace: true }) : undefined}
      />
    </div>
  );
}
